<template>
  <div v-if="currentTrack" class="audio-player-bar">
    <div class="player-content">
      <!-- 音频信息 -->
      <div class="track-info">
        <div class="track-cover" @click="handleToDetail">
          <div class="phonorecord">
            <svg-icon
              style="width: 100%; height: 100%"
              icon-class="phonorecord"
            />
          </div>
          <div class="default-cover">
            <img :src="playlistInfo.cover" />
          </div>
        </div>
        <div class="track-details">
          <div class="track-title">{{ currentTrack.name }}</div>
        </div>
      </div>

      <!-- 播放控制 -->
      <div class="player-controls">
        <!-- 上一首 -->
        <button
          class="control-btn"
          :class="{ disabled: !canPlayPrevious }"
          :disabled="!canPlayPrevious"
          @click="handlePlayPrevious"
        >
          <svg-icon icon-class="next" class="prev-icon next-icon" />
        </button>
        <!-- 播放/暂停 -->
        <div class="control-btn loading-btn" v-if="isLoading">
          <t-loading theme="circular" size="18px" class="wrapper" />
        </div>

        <div v-else class="control-btn" @click="handleTogglePlay">
          <svg-icon v-if="isPlaying" class="play-icon" icon-class="pause-bg" />

          <svg-icon class="play-icon" v-else icon-class="play-bg" />
        </div>

        <!-- 下一首 -->
        <button
          class="control-btn"
          :class="{ disabled: !canPlayNext }"
          :disabled="!canPlayNext"
          @click="handlePlayNext"
        >
          <svg-icon icon-class="next" class="next-icon" />
        </button>
      </div>

      <!-- 进度条 -->
      <div class="player-progress">
        <span class="time-text">{{ displayTime }}</span>
        <div class="progress-bar-wrapper">
          <vue-slider
            v-model="sliderValue"
            :max="maxDuration"
            :min="0"
            :step="1"
            :tooltip="'none'"
            :drag-on-click="true"
            @change="onProgressChange"
            @drag-end="handleChangeEnd"
          />
        </div>
        <span class="time-text">{{ formattedDuration }}</span>

        <div @click="handleShowPlaylist">
          <svg-icon icon-class="list" class="list-icon" />
        </div>
      </div>
    </div>
    <div
      class="play-list-mini"
      v-if="isShowPlayDialog"
      @mouseleave="mouseleaveMini"
    >
      <div class="listTitle">节目列表</div>
      <SinglePlayCard
        v-for="(item, index) in playlist"
        :key="index"
        :info="item"
        :courseInfo="playlistInfo"
        :chapterList="playlist"
      ></SinglePlayCard>
    </div>
  </div>
</template>

<script>
import audioMixin from "@/mixins/audioMixin";
import { formatTime } from "@/utils/common";
import SinglePlayCard from "../../components/column/singlePlayCard.vue";
import { getLessionsChapters, getAudiobookChapters } from "@/api/publicApi";
import VueSlider from "vue-slider-component";

import { mapActions } from "vuex";

export default {
  name: "AudioPlayerBar",

  components: {
    VueSlider,
    SinglePlayCard
  },

  mixins: [audioMixin],

  data() {
    return {
      isDragging: false,
      sliderValue: 0
    };
  },

  computed: {
    // 根据是否正在拖动来决定显示哪个时间
    displayTime() {
      if (this.isDragging) {
        // 拖动时显示拖动位置的时间
        return formatTime(this.sliderValue);
      } else {
        // 非拖动时显示实际播放时间
        return this.formattedCurrentTime;
      }
    },
    // 计算最大时长，确保至少为1
    maxDuration() {
      const duration = this.currentTrack?.duration || 1;
      return Math.max(1, duration);
    }
  },

  watch: {
    // 监听 currentTime 变化，同步到 slider（仅在非拖动时）
    currentTime(newTime) {
      if (!this.isDragging) {
        // 确保 sliderValue 不超过最大时长
        this.sliderValue = Math.min(newTime, this.maxDuration);
      }
    },
    // 监听当前音轨变化，重置 sliderValue
    currentTrack(newTrack) {
      if (newTrack) {
        // 当切换音轨时，确保 sliderValue 在有效范围内
        const max = this.maxDuration;
        this.sliderValue = Math.min(this.sliderValue || 0, max);
      }
    },
    // 监听最大时长变化，确保 sliderValue 不超过新的最大值
    maxDuration(newMax) {
      if (this.sliderValue > newMax) {
        this.sliderValue = newMax;
      }
    }
  },

  methods: {
    ...mapActions(["refreshBasicInfo"]),

    mouseleaveMini() {
      this.setShowPlayDialog(false);
    },
    handleToDetail() {
      // type 类型 0 默认 1 有声书 2 课程
      const { type, id } = this.playlistInfo;

      let query = {};
      if (type == 1) {
        query = { audioBookId: id };
      } else if (type == 2) {
        query = { lessionId: id };
      }

      // console.log("query: ", query);

      this.$router
        .push({
          path: "/column",
          query: query
        })
        .then(() => {
          window.scrollTo({
            top: 0
            // behavior: "smooth"
          });
        });

      // 刷新当前页面数据
      this.refreshBasicInfo();
    },
    getPlayList() {
      //1有声书2课程
      const { type, id } = this.playlistInfo;

      const fn = type === 1 ? getAudiobookChapters : getLessionsChapters;
      const params = {
        id,
        page: 1,
        pageSize: 99,
        sort: 0
      };
      fn(params).then(res => {
        if (res.code === 0) {
          const result = res.data;

          const chapterMap = new Map(
            result.chapters.map(chapter => {
              chapter.customId = `${type}-${chapter.fId}-${chapter.id}`;
              return [chapter.id, chapter];
            })
          );

          // 更新现有播放列表中的字段（除了 article 和 audioPath 保留原值，其他都更新）
          const updatedPlaylist = this.playlist.map(track => {
            const newChapter = chapterMap.get(track.id);

            const { duration, progress, playNumber, createTime } = newChapter;
            if (newChapter) {
              return {
                ...track, // 使用新数据的所有字段
                duration,
                progress,
                playNumber,
                createTime
                // article: track.article, // 保留原有的 article
                // audioPath: track.audioPath // 保留原有的 audioPath
              };
            }
            return track;
          });
          this.$EventBus.$emit("updatePlaylist", updatedPlaylist);
          this.setPlaylist({
            playlist: updatedPlaylist
            // info: courseDetail // 使用最新的课程详情信息
          });
        }
      });
    },
    handleShowPlaylist() {
      this.setShowPlayDialog(!this.isShowPlayDialog);
      if (this.isShowPlayDialog) {
        this.getPlayList();
      }
    },
    /**
     * 进度条拖动中
     * 只更新显示时间，不改变实际播放进度
     */
    onProgressChange() {
      // 标记正在拖动，防止 watch 更新 sliderValue
      this.isDragging = true;
      // sliderValue 通过 v-model 自动更新，displayTime 通过 computed 自动计算
    },

    /**
     * 进度条拖动结束
     * 使用 sliderValue 而不是参数，因为 v-model 已经绑定了值
     */
    handleChangeEnd() {
      // 确保 sliderValue 在有效范围内
      const clampedValue = Math.min(
        Math.max(0, this.sliderValue),
        this.maxDuration
      );
      this.sliderValue = clampedValue;
      // 先跳转到指定时间（真正改变播放进度）
      this.handleSeekTo(clampedValue);
      this.isDragging = false;
    }
  },
  mounted() {
    console.log(this.currentTrack, "currentTrackcurrentTrack");
    // 初始化 sliderValue，确保它在有效范围内
    if (this.currentTime !== undefined) {
      this.sliderValue = Math.min(
        Math.max(0, this.currentTime),
        this.maxDuration
      );
    }
  },

  beforeDestroy() {}
};
</script>

<style scoped lang="less">
@theme-color: #f87527;

.audio-player-bar {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 1200px;
  height: 60px;
  background: #ffffff;
  border-radius: 50px;
  border: 1px solid var(--94-, #efefef);
  box-shadow: 0 4px 20px 0 rgba(202, 207, 217, 0.2);
  backdrop-filter: blur(5px);
  z-index: 100;
}

.player-content {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  padding-right: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

/* 音频信息 */
.track-info {
  display: flex;
  align-items: center;
  gap: 28px;
  min-width: 200px;
  max-width: 340px;
}

.track-cover {
  width: 60px;
  height: 60px;
  position: relative;

  .default-cover {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
    border-radius: 7px;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .phonorecord {
    width: 60px;
    height: 60px;
    position: absolute;
    z-index: 0;
    box-shadow: 3px 0 15px 0 rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    left: 12px;
    svg-icon {
      width: 100%;
      height: 100%;
    }
  }
}

.track-details {
  flex: 1;
  min-width: 0;
}

.track-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.track-author {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 播放控制 */
.player-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.control-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;

  &.loading-btn {
    background: @theme-color;
    border-radius: 50%;
  }
  ::v-deep .t-loading__gradient-conic {
    background: conic-gradient(
      from 90deg,
      rgba(0, 82, 217, 0) 0deg,
      rgb(255, 255, 255) 360deg
    ) !important;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  .play-icon {
    width: 32px;
    height: 32px;
    color: @theme-color;
  }
}

.prev-icon {
  transform: rotate(180deg);
}

/* 进度条 */
.player-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  .list-icon {
    width: 24px;
    height: 24px;
    color: #262626;
    cursor: pointer;
    :hover {
      color: @theme-color;
    }
  }
}

.time-text {
  font-size: 12px;
  color: #666;
  min-width: 40px;
  text-align: center;
}

.progress-bar-wrapper {
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  ::v-deep .vue-slider-process {
    border-radius: 40px;
    background: var(
      ---,
      linear-gradient(94deg, #ff9c61 6.74%, @theme-color 94.25%)
    );
  }
  ::v-deep .vue-slider-rail {
    background: #f5f5f5;
  }
  ::v-deep .vue-slider-dot {
    width: 16px !important;
    height: 16px !important;
  }
  ::v-deep .vue-slider-dot-handle {
    border: 2px solid @theme-color;
    box-shadow: none;
  }
}

.next-icon {
  width: 16px;
  height: 16px;
}
.play-list-mini {
  padding: 32px;
  border-radius: 10px;
  border: 1px solid #efefef;
  background: #fff;
  box-shadow: 0 4px 20px 0 rgba(202, 207, 217, 0.2);
  position: absolute;
  bottom: 60px;
  right: 0;

  opacity: 1;
  transform: translateY(0);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  .listTitle {
    margin: 0;
    padding-bottom: 16px;
    margin-bottom: 24px;
    border-bottom: 1px solid #efefef;
  }
}
</style>
